import { type Data, Puck } from '@puckeditor/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';

import { adminApi } from '../lib/admin-api.ts';
import { useAuth } from '../lib/auth.tsx';
import { fromPuckData, toPuckData } from '../lib/page-document.ts';
import { getPageDraft, savePageDraft } from '../lib/pages.ts';
import { puckConfig } from '../lib/puck-config.tsx';

export const Route = createFileRoute('/pages/$pageId')({
  component: PageEditor,
});

function PageEditor() {
  const { pageId } = useParams({ from: '/pages/$pageId' });
  const { activeAccountId, can } = useAuth();
  const queryClient = useQueryClient();

  const { data: page, isLoading } = useQuery({
    queryKey: ['page', activeAccountId, pageId],
    queryFn: () => getPageDraft(activeAccountId as string, pageId),
    enabled: Boolean(activeAccountId),
    // The editor owns the document once mounted; refetching under it would
    // discard in-progress edits.
    refetchOnWindowFocus: false,
  });

  /**
   * Puck is uncontrolled once mounted, so the live document is tracked in a ref
   * rather than state -- putting it in state would re-render the whole editor
   * on every keystroke.
   */
  const liveData = useRef<Data | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const initialData = useMemo(() => (page ? toPuckData(page.sections) : null), [page]);

  const save = useMutation({
    mutationFn: async () => {
      const data = liveData.current;
      if (!data) return;
      await savePageDraft(activeAccountId as string, pageId, fromPuckData(data));
    },
    onSuccess: () => {
      setStatus('Draft saved');
      queryClient.invalidateQueries({ queryKey: ['pages', activeAccountId] });
    },
    onError: (error: Error) => setStatus(`Save failed: ${error.message}`),
  });

  const publish = useMutation({
    mutationFn: async () => {
      // Save first: publish copies whatever is currently in `sections`, so
      // publishing without saving would push the previous draft live.
      const data = liveData.current;
      if (data) {
        await savePageDraft(activeAccountId as string, pageId, fromPuckData(data));
      }
      await adminApi.pages.publish(activeAccountId as string, pageId);
    },
    onSuccess: () => {
      setStatus('Published');
      queryClient.invalidateQueries({ queryKey: ['pages', activeAccountId] });
    },
    onError: (error: Error) => setStatus(`Publish failed: ${error.message}`),
  });

  const handleChange = useCallback((data: Data) => {
    liveData.current = data;
  }, []);

  if (isLoading || !initialData) {
    return <div className="text-muted-foreground">Loading editor…</div>;
  }

  if (!page) {
    return <div className="text-muted-foreground">Page not found.</div>;
  }

  const canEdit = can('cms.manage');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/pages" className="text-sm text-muted-foreground hover:underline">
          ← Pages
        </Link>

        <div>
          <h1 className="text-lg font-semibold leading-tight">{page.title}</h1>
          <p className="text-xs text-muted-foreground">/{page.slug}</p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {status && <span className="text-xs text-muted-foreground">{status}</span>}

          <Button
            variant="outline"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!canEdit || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save draft'}
          </Button>

          <Button
            size="sm"
            onClick={() => publish.mutate()}
            disabled={!canEdit || publish.isPending}
          >
            {publish.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Puck ships its own full-height chrome, so it gets an explicit box. */}
      <div className="h-[calc(100vh-12rem)] overflow-hidden rounded-lg border">
        <Puck
          config={puckConfig}
          data={initialData}
          onChange={handleChange}
          /**
           * Puck's own header carries a second Publish button. The header above
           * already provides Save draft, Publish and the page's identity, so
           * Puck's is suppressed rather than leaving two of them on screen.
           */
          overrides={{ header: () => <></> }}
        />
      </div>
    </div>
  );
}
